import React, {
  useState,
  useEffect,
  useRef,
  useImperativeHandle,
  forwardRef,
  useCallback,
  useMemo,
} from "react";
import axios from "axios";
import { useSelector } from "react-redux";
import { useDbContext } from "../../../../context/DbContext";
import { selectActiveFyid } from "../../../../app/financialYearSlice";
import SherComboBox from "../../../ComboBoxes/SherComboBox";
import {
  FiPrinter,
  FiTag,
  FiFileText,
  FiSave,
  FiRotateCcw,
  FiTrash2,
  FiEdit3,
  FiCheck,
  FiSearch,
  FiX,
  FiHash,
  FiHelpCircle,
} from "react-icons/fi";

const PrinterSettings = forwardRef(({ onClose }, ref) => {
  const { dbCode } = useDbContext();
  const apiBaseUrl = process.env.REACT_APP_API_BASE_URL;
  const activeFyid = useSelector(selectActiveFyid);

  // Loading & UI states
  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);
  const [message, setMessage] = useState({ text: "", type: "" });
  const [searchQuery, setSearchQuery] = useState("");

  // Options from FormRegistration
  const [formOptions, setFormOptions] = useState([]);
  const [rawForms, setRawForms] = useState([]);

  // Existing PrintSettings records
  const [printSettingsList, setPrintSettingsList] = useState([]);

  // Active form state for editing / creating
  const [formData, setFormData] = useState({
    id: 0,
    auto: 0,
    dTransaction: "",
    invoiceLetter: "",
    invoiceSuffix: "",
    copies: 1,
    port: "",
    model: "",
    fyId: 0,
  });

  const formTypeComboRef = useRef(null);
  const prefixInputRef = useRef(null);
  const suffixInputRef = useRef(null);

  // ── 1. Fetch Form Types from FormRegistration ──────────────────────────────
  const fetchForms = useCallback(async () => {
    if (!dbCode) return;
    try {
      const res = await axios.get(`${apiBaseUrl}/main/getformregdatas/${dbCode}`);
      const data = res.data || [];
      setRawForms(data);

      const options = data.map((f) => {
        const name = String(f.fmrName || "").trim();
        return {
          label: name,
          value: name,
          formName: name,
          group: f.fmrTypeOfVoucher ? String(f.fmrTypeOfVoucher).trim() : "Other",
          voucherType: f.fmrTypeOfVoucher || "",
          abbr: f.fmrAbbreviation || "",
          startNo: f.fmrStartingEntryNo ?? 0,
        };
      });
      setFormOptions(options);
    } catch (err) {
      console.error("Error fetching form registration data:", err.message);
    }
  }, [apiBaseUrl, dbCode]);

  // ── 2. Fetch Existing PrintSettings ─────────────────────────────────────────
  const fetchPrintSettings = useCallback(async () => {
    if (!dbCode) return;
    setLoading(true);
    try {
      const res = await axios.get(`${apiBaseUrl}/main/printSettings/${dbCode}`);
      if (res.data?.success && Array.isArray(res.data.data)) {
        setPrintSettingsList(res.data.data);
      } else {
        setPrintSettingsList([]);
      }
    } catch (err) {
      console.error("Error fetching print settings:", err.message);
      setPrintSettingsList([]);
    } finally {
      setLoading(false);
    }
  }, [apiBaseUrl, dbCode]);

  useEffect(() => {
    if (dbCode) {
      fetchForms();
      fetchPrintSettings();
    }
  }, [dbCode, fetchForms, fetchPrintSettings]);

  // ── 3. Handle Form Selection from Combobox ──────────────────────────────────
  const handleSelectFormType = (opt) => {
    if (!opt) {
      handleClear();
      return;
    }

    // Extract strictly the form name, discarding any bracketed text like [SCHEME RECEIPT]
    let selectedFormName = "";
    if (typeof opt === "string") {
      selectedFormName = opt.split("[")[0].trim();
    } else if (typeof opt === "object") {
      selectedFormName = String(opt.formName || opt.value || opt.label || "").split("[")[0].trim();
    }

    // If empty or cleared (e.g. { label: "", value: "" }), reset form
    if (!selectedFormName || selectedFormName === "[object Object]") {
      handleClear();
      return;
    }

    // Check if a setting already exists for this form (strip any bracketed voucher from legacy rows)
    const existing = printSettingsList.find(
      (ps) =>
        String(ps.dTransaction || "").split("[")[0].trim().toLowerCase() ===
        selectedFormName.toLowerCase()
    );

    if (existing) {
      setFormData({
        id: existing.id || existing.auto || 0,
        auto: existing.auto || existing.id || 0,
        dTransaction: selectedFormName,
        invoiceLetter: existing.invoiceLetter || "",
        invoiceSuffix: existing.invoiceSuffix || "",
        copies: Number(existing.copies) || 1,
        port: existing.port || "",
        model: existing.model || "",
        fyId: existing.fyId || activeFyid || 0,
      });
      setMessage({
        text: `Loaded existing print settings for "${selectedFormName}"`,
        type: "info",
      });
    } else {
      // Suggest prefix from abbreviation if available
      const matchedForm = rawForms.find(
        (f) =>
          String(f.fmrName || "").trim().toLowerCase() ===
          selectedFormName.toLowerCase()
      );
      const suggestedPrefix = matchedForm?.fmrAbbreviation
        ? `${matchedForm.fmrAbbreviation}/`
        : "";

      setFormData({
        id: 0,
        auto: 0,
        dTransaction: selectedFormName,
        invoiceLetter: suggestedPrefix,
        invoiceSuffix: "",
        copies: 1,
        port: "",
        model: "",
        fyId: activeFyid || 0,
      });
      setMessage({
        text: `Configure invoice prefix & suffix for "${selectedFormName}"`,
        type: "info",
      });
    }

    setTimeout(() => {
      prefixInputRef.current?.focus();
      prefixInputRef.current?.select?.();
    }, 100);
  };

  // ── 4. Clear Form ───────────────────────────────────────────────────────────
  const handleClear = () => {
    setFormData({
      id: 0,
      auto: 0,
      dTransaction: "",
      invoiceLetter: "",
      invoiceSuffix: "",
      copies: 1,
      port: "",
      model: "",
      fyId: activeFyid || 0,
    });
    setMessage({ text: "", type: "" });
  };

  // ── 5. Save Print Settings ──────────────────────────────────────────────────
  const handleSave = async () => {
    const cleanFormName = String(formData.dTransaction || "").split("[")[0].trim();
    if (!cleanFormName) {
      alert("Please select or enter a Form Voucher Type.");
      return;
    }

    setSaving(true);
    try {
      const payload = {
        ...formData,
        dTransaction: cleanFormName,
        invoiceLetter: formData.invoiceLetter ? formData.invoiceLetter.trim() : "",
        invoiceSuffix: formData.invoiceSuffix ? formData.invoiceSuffix.trim() : "",
        copies: Number(formData.copies) || 1,
        fyId: activeFyid || formData.fyId || 0,
      };

      const res = await axios.post(
        `${apiBaseUrl}/main/printSettings/${dbCode}`,
        payload
      );

      if (res.data?.success) {
        setMessage({
          text: `Invoice letters for "${cleanFormName}" saved successfully!`,
          type: "success",
        });
        await fetchPrintSettings();
      } else {
        alert(res.data?.message || "Failed to save print settings.");
      }
    } catch (err) {
      console.error("Error saving print settings:", err.message);
      alert("Error saving print settings: " + err.message);
    } finally {
      setSaving(false);
    }
  };

  // ── 6. Delete Setting ───────────────────────────────────────────────────────
  const handleDelete = async (autoId, formName) => {
    const cleanFormName = String(formName || "").split("[")[0].trim();
    if (
      !window.confirm(
        `Are you sure you want to remove print settings for "${cleanFormName}"?`
      )
    ) {
      return;
    }

    try {
      const res = await axios.delete(
        `${apiBaseUrl}/main/printSettings/${dbCode}/${autoId}`
      );
      if (res.data?.success) {
        setMessage({
          text: `Settings for "${cleanFormName}" deleted successfully.`,
          type: "success",
        });
        if (formData.auto === autoId || formData.id === autoId) {
          handleClear();
        }
        await fetchPrintSettings();
      }
    } catch (err) {
      console.error("Error deleting print setting:", err.message);
      alert("Error deleting record: " + err.message);
    }
  };

  // ── 7. Select Row from Grid to Edit ─────────────────────────────────────────
  const handleSelectRecord = (record) => {
    const cleanFormName = String(record.dTransaction || "").split("[")[0].trim();
    setFormData({
      id: record.id || record.auto || 0,
      auto: record.auto || record.id || 0,
      dTransaction: cleanFormName,
      invoiceLetter: record.invoiceLetter || "",
      invoiceSuffix: record.invoiceSuffix || "",
      copies: Number(record.copies) || 1,
      port: record.port || "",
      model: record.model || "",
      fyId: record.fyId || 0,
    });
    setMessage({
      text: `Editing invoice letters for "${cleanFormName}"`,
      type: "info",
    });
    prefixInputRef.current?.focus();
  };

  // Expose delegate methods to parent SoftwareSettingsRoot
  useImperativeHandle(ref, () => ({
    handleSave,
    fetchSettings: fetchPrintSettings,
  }));

  // Filter existing configurations
  const filteredSettings = useMemo(() => {
    if (!searchQuery.trim()) return printSettingsList;
    const needle = searchQuery.toLowerCase().trim();
    return printSettingsList.filter(
      (ps) =>
        (ps.dTransaction && ps.dTransaction.toLowerCase().includes(needle)) ||
        (ps.invoiceLetter && ps.invoiceLetter.toLowerCase().includes(needle)) ||
        (ps.invoiceSuffix && ps.invoiceSuffix.toLowerCase().includes(needle)) ||
        (ps.port && ps.port.toLowerCase().includes(needle))
    );
  }, [printSettingsList, searchQuery]);

  // Live formatted invoice number preview
  const previewInvoiceNo = useMemo(() => {
    const prefix = formData.invoiceLetter || "";
    const suffix = formData.invoiceSuffix || "";
    const sampleDigits = "000101";
    return `${prefix}${sampleDigits}${suffix}`;
  }, [formData.invoiceLetter, formData.invoiceSuffix]);

  return (
    <div className="w-full h-full flex flex-col gap-3 font-['Inter',sans-serif] text-slate-700">
      
      {/* ── Top Overview Banner ── */}
      <div className="bg-white px-4 py-3 rounded-xl border border-slate-200/90 shadow-2xs flex flex-col sm:flex-row items-start sm:items-center justify-between gap-2 shrink-0">
        <div className="flex items-center gap-2.5">
          <div className="w-9 h-9 rounded-lg bg-blue-50 border border-blue-200 text-blue-700 flex items-center justify-center shrink-0">
            <FiPrinter className="w-4.5 h-4.5" />
          </div>
          <div>
            <h2 className="text-sm font-bold text-slate-800 leading-tight">
              Form Series &amp; Invoice Letter Configuration
            </h2>
            <p className="text-[11.5px] text-slate-500">
              Set custom invoice prefix letters and suffix codes for registered form voucher types. Saved in <code className="font-mono text-blue-700 font-bold">PrintSettings</code> table.
            </p>
          </div>
        </div>

        {/* Live Status Badge */}
        {message.text && (
          <div
            className={`text-xs px-3 py-1 rounded-md font-semibold flex items-center gap-1.5 transition-all ${
              message.type === "success"
                ? "bg-emerald-50 text-emerald-700 border border-emerald-200"
                : "bg-blue-50 text-blue-700 border border-blue-200"
            }`}
          >
            <FiCheck className="text-xs" />
            <span>{message.text}</span>
          </div>
        )}
      </div>

      {/* ── Middle Split Section: Form Editor (Left) & Configured List (Right) ── */}
      <div className="flex-1 flex flex-col lg:flex-row gap-3 min-h-0 overflow-hidden items-stretch">
        
        {/* LEFT CARD: Form Entry Editor */}
        <div className="w-full lg:w-[480px] bg-white p-4 rounded-xl border border-slate-200/90 shadow-2xs flex flex-col gap-3.5 shrink-0 overflow-y-auto report-scroll">
          
          <div className="flex items-center justify-between pb-2 border-b border-slate-100">
            <div className="flex items-center gap-1.5 text-[#0a3899] font-bold text-xs uppercase tracking-wider">
              <FiTag className="text-xs" />
              <span>Configure Form Invoice Letters</span>
            </div>
            {formData.auto > 0 && (
              <span className="text-[10px] font-bold px-2 py-0.5 rounded-full bg-amber-50 text-amber-800 border border-amber-200">
                Editing #{formData.auto}
              </span>
            )}
          </div>

          {/* Field 1: Form Type Combobox (Loaded from FormRegistration) */}
          <div className="flex flex-col gap-1">
            <label className="text-xs font-bold text-slate-700 flex items-center justify-between">
              <span>Select Form / Voucher Series <span className="text-red-500">*</span></span>
              <span className="text-[10.5px] font-normal text-slate-400">From Form Registration</span>
            </label>
            <SherComboBox
              comboRef={formTypeComboRef}
              options={formOptions}
              value={formData.dTransaction}
              onSelect={handleSelectFormType}
              onClear={handleClear}
              onInputChange={(text) => {
                const raw = typeof text === "string" ? text : (text?.formName || text?.value || text?.label || "");
                const val = raw.split("[")[0].trim();
                setFormData((prev) => ({ ...prev, dTransaction: val }));
                if (!val) setMessage({ text: "", type: "" });
              }}
              onFreeInput={(text) => {
                const raw = typeof text === "string" ? text : (text?.formName || text?.value || text?.label || "");
                const val = raw.split("[")[0].trim();
                setFormData((prev) => ({ ...prev, dTransaction: val }));
              }}
              placeholder="Search or select form type..."
              showArrow={true}
              clearable={true}
              inputWidth="100%"
              inputHeight="32px"
              inputClassName="border border-slate-300 rounded-lg focus:border-blue-600 text-xs pl-3 font-semibold hover:border-slate-400 transition-all bg-white"
            />
          </div>

          {/* Row 2: Prefix and Suffix Fields */}
          <div className="grid grid-cols-2 gap-3">
            {/* Prefix Field */}
            <div className="flex flex-col gap-1">
              <label className="text-xs font-bold text-slate-700 flex items-center gap-1">
                <span>Invoice Prefix</span>
                <span className="text-slate-400 font-normal text-[10.5px]">(invoiceletter)</span>
              </label>
              <div className="relative flex items-center">
                <input
                  type="text"
                  ref={prefixInputRef}
                  value={formData.invoiceLetter}
                  onChange={(e) =>
                    setFormData((prev) => ({
                      ...prev,
                      invoiceLetter: e.target.value.toUpperCase(),
                    }))
                  }
                  onKeyDown={(e) => {
                    if (e.key === "Enter") suffixInputRef.current?.focus();
                  }}
                  placeholder="e.g. REC/ or INV-"
                  className="w-full h-8 px-2.5 text-xs font-mono font-bold border border-slate-300 rounded-lg outline-none focus:border-blue-600 focus:ring-1 focus:ring-blue-600/20 bg-white hover:border-slate-400 transition-all uppercase"
                />
              </div>
              <span className="text-[10px] text-slate-400">Placed before number</span>
            </div>

            {/* Suffix Field */}
            <div className="flex flex-col gap-1">
              <label className="text-xs font-bold text-slate-700 flex items-center gap-1">
                <span>Invoice Suffix</span>
                <span className="text-slate-400 font-normal text-[10.5px]">(invoicesuffix)</span>
              </label>
              <div className="relative flex items-center">
                <input
                  type="text"
                  ref={suffixInputRef}
                  value={formData.invoiceSuffix}
                  onChange={(e) =>
                    setFormData((prev) => ({
                      ...prev,
                      invoiceSuffix: e.target.value.toUpperCase(),
                    }))
                  }
                  placeholder="e.g. /24-25 or -A"
                  className="w-full h-8 px-2.5 text-xs font-mono font-bold border border-slate-300 rounded-lg outline-none focus:border-blue-600 focus:ring-1 focus:ring-blue-600/20 bg-white hover:border-slate-400 transition-all uppercase"
                />
              </div>
              <span className="text-[10px] text-slate-400">Placed after number</span>
            </div>
          </div>

          {/* Interactive Live Preview Box */}
          <div className="p-3 bg-blue-50/60 rounded-lg border border-blue-100 flex flex-col gap-1.5">
            <div className="flex items-center justify-between text-[11px] font-bold text-blue-900">
              <span className="flex items-center gap-1">
                <FiHash className="text-xs text-blue-600" />
                <span>Live Sample Invoice Number Preview:</span>
              </span>
              <span className="text-[10px] font-mono text-blue-600 uppercase font-semibold">
                Format Check
              </span>
            </div>
            <div className="bg-white p-2 rounded-md border border-blue-200 text-center font-mono font-extrabold text-sm text-slate-800 tracking-wider shadow-2xs">
              <span className="text-blue-700">{formData.invoiceLetter || ""}</span>
              <span className="text-slate-900 underline decoration-blue-400 decoration-2">
                000101
              </span>
              <span className="text-emerald-700">{formData.invoiceSuffix || ""}</span>
            </div>
            <p className="text-[10.5px] text-slate-500 italic text-center">
              Example generated invoice string when printing or recording transactions.
            </p>
          </div>

          {/* Action Buttons */}
          <div className="flex items-center gap-2 pt-2 border-t border-slate-100 mt-auto">
            <button
              type="button"
              onClick={handleClear}
              className="flex-1 h-8.5 bg-slate-100 hover:bg-slate-200 text-slate-700 font-bold text-xs rounded-lg transition-all cursor-pointer flex items-center justify-center gap-1.5"
            >
              <FiRotateCcw className="text-xs" />
              <span>Reset</span>
            </button>
            <button
              type="button"
              onClick={handleSave}
              disabled={saving}
              className="flex-2 h-8.5 bg-[#1e40af] hover:bg-blue-800 active:scale-[0.99] text-white font-bold text-xs rounded-lg shadow-sm transition-all cursor-pointer flex items-center justify-center gap-1.5 disabled:opacity-50"
            >
              <FiSave className="text-xs" />
              <span>{saving ? "Saving..." : formData.auto > 0 ? "Update Letters" : "Save Letters"}</span>
            </button>
          </div>
        </div>

        {/* RIGHT CARD: Configured Forms Table (Data Grid) */}
        <div className="flex-1 bg-white rounded-xl border border-slate-200/90 shadow-2xs flex flex-col min-h-0 overflow-hidden">
          
          {/* Table Header Bar with Search */}
          <div className="h-10 px-3.5 border-b border-slate-200 flex items-center justify-between bg-slate-50/80 shrink-0">
            <div className="flex items-center gap-2">
              <span className="text-xs font-bold text-[#0a3899] uppercase tracking-wider flex items-center gap-1.5">
                <FiFileText className="text-xs" />
                <span>Configured Forms in PrintSettings</span>
              </span>
              <span className="text-[11px] font-bold px-2 py-0.5 rounded-full bg-blue-100/70 text-blue-800 font-mono">
                {filteredSettings.length}
              </span>
            </div>

            {/* Search Input */}
            <div className="relative flex items-center w-60">
              <FiSearch className="absolute left-2.5 text-slate-400 text-xs pointer-events-none" />
              <input
                type="text"
                placeholder="Search configured forms..."
                value={searchQuery}
                onChange={(e) => setSearchQuery(e.target.value)}
                className="w-full h-7 pl-7 pr-6 text-xs border border-slate-300 rounded-md outline-none focus:border-blue-600 bg-white"
              />
              {searchQuery && (
                <button
                  type="button"
                  onClick={() => setSearchQuery("")}
                  className="absolute right-2 text-slate-400 hover:text-slate-600 text-xs"
                >
                  <FiX />
                </button>
              )}
            </div>
          </div>

          {/* Table Body */}
          <div className="flex-1 overflow-auto report-scroll min-h-0 bg-white">
            <table className="w-full text-left border-collapse text-xs font-['Inter',sans-serif]">
              <thead className="bg-[#0f56d3] text-white sticky top-0 font-semibold z-10 shadow-xs">
                <tr className="h-7.5">
                  <th className="py-1 px-3 font-semibold text-[11px] border-r border-blue-400/40 min-w-[160px] whitespace-nowrap">
                    Form Series (DTransaction)
                  </th>
                  <th className="py-1 px-3 font-semibold text-[11px] border-r border-blue-400/40 w-[110px] whitespace-nowrap">
                    Prefix (invoiceletter)
                  </th>
                  <th className="py-1 px-3 font-semibold text-[11px] border-r border-blue-400/40 w-[110px] whitespace-nowrap">
                    Suffix (invoicesuffix)
                  </th>
                  <th className="py-1 px-3 font-semibold text-[11px] border-r border-blue-400/40 min-w-[140px] whitespace-nowrap">
                    Sample Format
                  </th>
                  <th className="py-1 px-2 font-semibold text-[11px] text-center w-[80px] whitespace-nowrap">
                    Action
                  </th>
                </tr>
              </thead>
              <tbody className="divide-y divide-slate-200 font-medium text-slate-800">
                {filteredSettings.length === 0 ? (
                  <tr>
                    <td
                      colSpan="5"
                      className="py-12 text-center text-xs text-slate-400 italic bg-white"
                    >
                      {loading
                        ? "Loading print settings..."
                        : "No print settings configured yet. Select a form on the left to set invoice letters."}
                    </td>
                  </tr>
                ) : (
                  filteredSettings.map((row, idx) => {
                    const isSelected =
                      String(row.dTransaction || "").trim().toLowerCase() ===
                      String(formData.dTransaction || "").trim().toLowerCase();

                    const sample = `${row.invoiceLetter || ""}000101${
                      row.invoiceSuffix || ""
                    }`;

                    return (
                      <tr
                        key={row.auto || row.id || idx}
                        onClick={() => handleSelectRecord(row)}
                        className={`h-8 border-b border-slate-200 hover:bg-blue-50/60 transition-colors duration-150 cursor-pointer ${
                          isSelected
                            ? "bg-blue-100/80 text-blue-900 font-semibold ring-1 ring-inset ring-blue-400/60"
                            : idx % 2 === 0
                            ? "bg-white"
                            : "bg-slate-50/70"
                        }`}
                      >
                        <td className="py-1 px-3 border-r border-slate-200/80 font-bold text-slate-850 whitespace-nowrap">
                          {String(row.dTransaction || "").split("[")[0].trim()}
                        </td>
                        <td className="py-1 px-3 border-r border-slate-200/80 font-mono font-bold text-blue-700 whitespace-nowrap">
                          {row.invoiceLetter || <span className="text-slate-300">-</span>}
                        </td>
                        <td className="py-1 px-3 border-r border-slate-200/80 font-mono font-bold text-emerald-700 whitespace-nowrap">
                          {row.invoiceSuffix || <span className="text-slate-300">-</span>}
                        </td>
                        <td className="py-1 px-3 border-r border-slate-200/80 font-mono font-extrabold text-slate-800 whitespace-nowrap">
                          <span className="px-1.5 py-0.5 rounded bg-slate-100 border border-slate-200 text-[11px]">
                            {sample}
                          </span>
                        </td>
                        <td className="py-1 px-2 text-center whitespace-nowrap">
                          <div className="flex items-center justify-center gap-1">
                            <button
                              type="button"
                              onClick={(e) => {
                                e.stopPropagation();
                                handleSelectRecord(row);
                              }}
                              title="Edit Settings"
                              className="w-6 h-6 rounded flex items-center justify-center text-blue-600 hover:bg-blue-100 transition-colors"
                            >
                              <FiEdit3 className="text-xs" />
                            </button>
                            <button
                              type="button"
                              onClick={(e) => {
                                e.stopPropagation();
                                handleDelete(row.auto || row.id, row.dTransaction);
                              }}
                              title="Delete Settings"
                              className="w-6 h-6 rounded flex items-center justify-center text-red-600 hover:bg-red-100 transition-colors"
                            >
                              <FiTrash2 className="text-xs" />
                            </button>
                          </div>
                        </td>
                      </tr>
                    );
                  })
                )}
              </tbody>
            </table>
          </div>

          {/* Table Footer Status */}
          <div className="h-7.5 px-3.5 bg-slate-50 border-t border-slate-200 flex items-center justify-between text-[11px] text-slate-500 shrink-0">
            <span className="flex items-center gap-1 text-slate-400">
              <FiHelpCircle className="text-xs" />
              <span>Click any record to load and modify its invoice prefix/suffix settings</span>
            </span>
            <span className="font-mono font-bold text-slate-600">
              Total Configured: {filteredSettings.length}
            </span>
          </div>
        </div>
      </div>
    </div>
  );
});

export default PrinterSettings;
